import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';

const BasicInfoEditor = ({ project, onChange }) => {
  const { t } = useLanguage();
  
  const handleChange = (field, value) => {
    onChange({ ...project, [field]: value });
  };
  
  const handleBuildingSummaryChange = (field, value) => {
    onChange({
      ...project,
      building_summary: {
        ...(project.building_summary || {}),
        [field]: value
      }
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Basic Project Information
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Edit core project details and building information
        </p>
      </div>
      
      {/* Project Details */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Project Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              value={project.name}
              onChange={(e) => handleChange('name', e.target.value)}
              data-testid="input-name"
            />
          </div>
          
          <div>
            <Label htmlFor="project_number">Project Number</Label>
            <Input
              id="project_number"
              value={project.project_number || ''}
              onChange={(e) => handleChange('project_number', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="project_type">Project Type</Label>
            <Input
              id="project_type"
              value={t(project.project_type)}
              disabled
              className="bg-slate-50 dark:bg-slate-800"
            />
          </div>
        </div>
      </Card>
      
      {/* Location */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Location
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={project.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={project.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              value={project.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="zip_code">Zip Code</Label>
            <Input
              id="zip_code"
              value={project.zip_code || ''}
              onChange={(e) => handleChange('zip_code', e.target.value)}
            />
          </div>
        </div>
      </Card>
      
      {/* Client/Owner */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Client / Owner
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="client_name">Client Name</Label>
            <Input
              id="client_name"
              value={project.client_name || ''}
              onChange={(e) => handleChange('client_name', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="client_phone">Client Phone</Label>
            <Input
              id="client_phone"
              value={project.client_phone || ''}
              onChange={(e) => handleChange('client_phone', e.target.value)}
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="client_address">Client Address</Label>
            <Input
              id="client_address"
              value={project.client_address || ''}
              onChange={(e) => handleChange('client_address', e.target.value)}
            />
          </div>
        </div>
      </Card>
      
      {/* Architect */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Architect
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="architect_name">Architect Name</Label>
            <Input
              id="architect_name"
              value={project.architect_name || ''}
              onChange={(e) => handleChange('architect_name', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="architect_company">Company</Label>
            <Input
              id="architect_company"
              value={project.architect_company || ''}
              onChange={(e) => handleChange('architect_company', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="architect_phone">Phone</Label>
            <Input
              id="architect_phone"
              value={project.architect_phone || ''}
              onChange={(e) => handleChange('architect_phone', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="architect_license">License Number</Label>
            <Input
              id="architect_license"
              value={project.architect_license || ''}
              onChange={(e) => handleChange('architect_license', e.target.value)}
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="architect_address">Address</Label>
            <Input
              id="architect_address"
              value={project.architect_address || ''}
              onChange={(e) => handleChange('architect_address', e.target.value)}
            />
          </div>
        </div>
      </Card>
      
      {/* Building Summary */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Building Summary
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="existing_sf">Existing SF</Label>
            <Input
              id="existing_sf"
              type="number"
              value={project.building_summary?.existing_sf || ''}
              onChange={(e) => handleBuildingSummaryChange('existing_sf', parseFloat(e.target.value))}
            />
          </div>
          
          <div>
            <Label htmlFor="addition_sf">Addition SF</Label>
            <Input
              id="addition_sf"
              type="number"
              value={project.building_summary?.addition_sf || ''}
              onChange={(e) => handleBuildingSummaryChange('addition_sf', parseFloat(e.target.value))}
            />
          </div>
          
          <div>
            <Label htmlFor="total_sf">Total SF</Label>
            <Input
              id="total_sf"
              type="number"
              value={project.building_summary?.total_sf || ''}
              onChange={(e) => handleBuildingSummaryChange('total_sf', parseFloat(e.target.value))}
            />
          </div>
          
          <div>
            <Label htmlFor="construction_type">Construction Type</Label>
            <Input
              id="construction_type"
              value={project.building_summary?.construction_type || ''}
              onChange={(e) => handleBuildingSummaryChange('construction_type', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="occupancy">Occupancy</Label>
            <Input
              id="occupancy"
              value={project.building_summary?.occupancy || ''}
              onChange={(e) => handleBuildingSummaryChange('occupancy', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="zoning">Zoning</Label>
            <Input
              id="zoning"
              value={project.building_summary?.zoning || ''}
              onChange={(e) => handleBuildingSummaryChange('zoning', e.target.value)}
            />
          </div>
        </div>
      </Card>
      
      {/* Scope of Work */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Scope of Work
        </h3>
        
        <Textarea
          value={project.scope_of_work || ''}
          onChange={(e) => handleChange('scope_of_work', e.target.value)}
          placeholder="Describe the scope of work for this project..."
          rows={8}
          data-testid="textarea-scope"
        />
      </Card>
    </div>
  );
};

export default BasicInfoEditor;